<template>
    <Transition name="page-transition-loader">
        <OrbitingParticles v-if="visible" overlay :aria-label="t('components.pageTransitionLoader.label')" />
    </Transition>
</template>

<script setup lang="ts">
import { Capacitor } from "@capacitor/core";
import { router } from "@inertiajs/vue3";
import { useTimeoutFn } from "@vueuse/core";
import OrbitingParticles from "~vue/components/OrbitingParticles.vue";
import { onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const SHOW_DELAY_MS = 450;
const MIN_VISIBLE_MS = 400;

const visible = ref(false);
const hideDelayMs = ref(MIN_VISIBLE_MS);
let shownAt: number | null = null;
let unsubscribers: Array<() => void> = [];

const isNative = Capacitor.isNativePlatform();

const {
    start: startShow,
    stop: stopShow,
    isPending: isShowPending,
} = useTimeoutFn(
    () => {
        visible.value = true;
        shownAt = Date.now();
    },
    SHOW_DELAY_MS,
    { immediate: false },
);

const { start: startHide } = useTimeoutFn(
    () => {
        visible.value = false;
        shownAt = null;
    },
    hideDelayMs,
    { immediate: false },
);

function show() {
    if (isShowPending.value || visible.value) {
        return;
    }
    startShow();
}

function hide() {
    stopShow();
    if (!visible.value) {
        return;
    }
    const elapsed = shownAt === null ? MIN_VISIBLE_MS : Date.now() - shownAt;
    hideDelayMs.value = Math.max(0, MIN_VISIBLE_MS - elapsed);
    startHide();
}

onMounted(() => {
    if (!isNative) {
        return;
    }
    unsubscribers = [router.on("start", show), router.on("finish", hide), router.on("error", hide)];
});

onUnmounted(() => {
    unsubscribers.forEach((unsubscribe) => unsubscribe());
});
</script>

<style scoped>
.page-transition-loader-enter-active,
.page-transition-loader-leave-active {
    transition: opacity 150ms ease-out;
}

.page-transition-loader-enter-from,
.page-transition-loader-leave-to {
    opacity: 0;
}
</style>
